import { selectCurrentHotelName } from 'accessors'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import ChevronIcon from 'components/ReusableComponents/Icons/Chevron'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import contentApi from 'services/api/content'

import ActionCard from './ActionCard'
import styles from './CartAbandonmentFlow.module.scss'
import ConditionNode from './ConditionNode'
import EditActionDialog from './EditActionDialog'
import NodeConnector from './NodeConnector'
import TriggerCard from './TriggerCard'

function CartAbandonmentFlow() {
  const [analytics, setAnalytics] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [editingCard, setEditingCard] = useState(null)

  const { addToast } = useToasts()

  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)
  const isDemoMode = useSelector(s => s.login.isDemoMode)

  const fetchAnalytics = () => {
    setIsLoading(true)
    contentApi
      .getAnalytics()
      .then(data => {
        setAnalytics(data)
      })
      .catch(error => {
        console.error('Failed to fetch analytics:', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    fetchAnalytics()
  }, [])

  const replaceTags = text => {
    if (!text) return ''

    const firstName = userName?.substring(0, userName?.indexOf(' ')) || 'Guest'
    const lastName = userName?.split(' ').pop() || ''
    const displayHotelName = isDemoMode ? 'Directful Demo Hotel' : hotelName

    return text
      .replace(/@\[(\S+)\]\((__)?(\S+)(__)?\)/gm, '$1')
      .replace('guestName', firstName)
      .replace('guestLastName', lastName)
      .replace('hotelName', displayHotelName)
      .replace('promoName', 'Special Offer')
  }

  const handleCardClick = cardId => {
    setEditingCard(cardId)
  }

  const handleDialogClose = () => {
    setEditingCard(null)
  }

  const getNotificationData = cardId => {
    if (cardId === 'welcome') {
      return analytics.welcomeMessage.notification
    }
    if (cardId === 'followUp1') {
      return analytics.followUp1.notification
    }
    return analytics.followUp2.notification
  }

  const convertToHours = (value, unit) => {
    return unit === 'days' ? value * 24 : value
  }

  const convertFromHours = hours => {
    if (!hours) return { value: 48, unit: 'days', displayValue: 2 }
    if (hours % 24 === 0) {
      return { value: hours, unit: 'days', displayValue: hours / 24 }
    }
    return { value: hours, unit: 'hours', displayValue: hours }
  }

  const persistConfiguration = async data => {
    const notification = getNotificationData(editingCard)
    const notificationContentId = notification?.notificationContentId

    if (!notificationContentId) {
      throw new Error('Missing notification ID')
    }

    await contentApi.updateConfiguration(notificationContentId, {
      messageTemplate: data.messageTemplate,
      isActive: data.isEnabled,
      medium: data.medium,
      hoursAfterPreviousCampaign: convertToHours(
        data.sendAfterValue,
        data.sendAfterUnit
      ),
      messageMedia: data.messageMedia
    })
  }

  const handleDialogSave = async data => {
    setIsSaving(true)
    try {
      await persistConfiguration(data)

      addToast('Configuration saved successfully', {
        appearance: 'success',
        autoDismiss: true
      })

      setEditingCard(null)
      fetchAnalytics()
    } catch (error) {
      console.error('Failed to save configuration:', error)
      addToast(
        error?.message === 'Missing notification ID'
          ? 'Unable to save: missing notification ID'
          : 'Failed to save configuration',
        {
          appearance: 'error',
          autoDismiss: true
        }
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleDialogPersistDraft = async data => {
    try {
      await persistConfiguration(data)
    } catch (error) {
      console.error('Failed to persist draft before test send:', error)
      throw error
    }
  }

  const getDialogTitle = cardId => {
    switch (cardId) {
      case 'welcome':
        return 'Welcome message'
      case 'followUp1':
        return 'Widget follow up 1'
      case 'followUp2':
        return 'Widget follow up 2'
      default:
        return ''
    }
  }

  // API expects a different contentCategory per step for test email (no contentId in request)
  const getContentCategory = cardId => {
    switch (cardId) {
      case 'welcome':
        return 'CardAbandonmentOptedIn'
      case 'followUp1':
        return 'CardAbandonmentNotBookedFollowUp1'
      case 'followUp2':
        return 'CardAbandonmentNotBookedFollowUp2'
      default:
        return 'CartAbandonment'
    }
  }

  const getConditionLabel = hours => {
    const { unit, displayValue } = convertFromHours(hours)
    const singularUnit = unit === 'days' ? 'day' : 'hour'
    return {
      value: displayValue,
      unit: displayValue === 1 ? singularUnit : unit
    }
  }

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (!analytics) {
    return (
      <div>
        <CustomHeader width={900}>
          <div className={styles.header}>
            <Link to="/site-abandonment">Site abandonment</Link>
            <ChevronIcon /> Campaign flow
          </div>
        </CustomHeader>
        <div className={styles.wrapper}>
          <div>Failed to load analytics data</div>
        </div>
      </div>
    )
  }

  const editingNotification = editingCard ? getNotificationData(editingCard) : null
  const isFollowUp2DisabledByPrevious = !analytics.followUp1.notification?.isActive

  return (
    <div>
      <CustomHeader width={900}>
        <div className={styles.header}>
          <Link to="/site-abandonment">Site abandonment</Link>
          <ChevronIcon /> Campaign flow
        </div>
      </CustomHeader>
      <div className={styles.wrapper}>
        <TriggerCard title="Guest signed up" stats={analytics.guestSignedUp} />
        <NodeConnector />
        <ActionCard
          title="Send welcome message"
          messagePreview={replaceTags(
            analytics.welcomeMessage.notification?.messageTemplate
          )}
          stats={analytics.welcomeMessage}
          isActive={analytics.welcomeMessage.notification?.isActive}
          hasEmailCampaign={analytics.welcomeMessage.notification?.hasEmailCampaign}
          onClick={() => handleCardClick('welcome')}
        />
        <NodeConnector />
        <ConditionNode
          value={
            getConditionLabel(
              analytics.followUp1.notification?.hoursAfterPreviousCampaign
            ).value
          }
          unit={
            getConditionLabel(
              analytics.followUp1.notification?.hoursAfterPreviousCampaign
            ).unit
          }
          onClick={() => handleCardClick('followUp1')}
        />
        <NodeConnector hasArrow />
        <ActionCard
          title="Send first follow up"
          messagePreview={replaceTags(analytics.followUp1.notification?.messageTemplate)}
          stats={analytics.followUp1}
          isActive={analytics.followUp1.notification?.isActive}
          hasEmailCampaign={analytics.followUp1.notification?.hasEmailCampaign}
          onClick={() => handleCardClick('followUp1')}
        />
        <NodeConnector isDisabledByPrevious={isFollowUp2DisabledByPrevious} />
        <ConditionNode
          value={
            getConditionLabel(
              analytics.followUp2.notification?.hoursAfterPreviousCampaign
            ).value
          }
          unit={
            getConditionLabel(
              analytics.followUp2.notification?.hoursAfterPreviousCampaign
            ).unit
          }
          onClick={() => handleCardClick('followUp2')}
          isDisabledByPrevious={isFollowUp2DisabledByPrevious}
        />
        <NodeConnector hasArrow isDisabledByPrevious={isFollowUp2DisabledByPrevious} />
        <ActionCard
          title="Send second follow up"
          messagePreview={replaceTags(analytics.followUp2.notification?.messageTemplate)}
          stats={analytics.followUp2}
          isActive={analytics.followUp2.notification?.isActive}
          hasEmailCampaign={analytics.followUp2.notification?.hasEmailCampaign}
          onClick={() => handleCardClick('followUp2')}
          isDisabledByPrevious={isFollowUp2DisabledByPrevious}
        />

        {editingCard && (
          <EditActionDialog
            key={editingCard}
            contentId={editingNotification?.notificationContentId}
            contentCategory={getContentCategory(editingCard)}
            title={getDialogTitle(editingCard)}
            onClose={handleDialogClose}
            onSave={handleDialogSave}
            onPersistDraft={handleDialogPersistDraft}
            isSaving={isSaving}
            showConfigurationStep={editingCard !== 'welcome'}
            initialData={{
              isEnabled: editingNotification?.isActive,
              sendAfterValue: convertFromHours(
                editingNotification?.hoursAfterPreviousCampaign
              ).displayValue,
              sendAfterUnit: convertFromHours(
                editingNotification?.hoursAfterPreviousCampaign
              ).unit,
              medium: editingNotification?.medium,
              messageTemplate: editingNotification?.messageTemplate,
              messageMedia: editingNotification?.messageMediaUrls?.[0] ?? null
            }}
          />
        )}
      </div>
    </div>
  )
}

export default CartAbandonmentFlow
